import { Component, Vue, Prop, Inject, Watch } from 'vue-property-decorator';
import { Multiselect } from 'vue-multiselect';
import { SelectListItemWithSubtext, createSelectListItemWithSubtext, createSelectListItemWithSubtextFromAttendee, AttendeeSelectListItem, DtoAddress } from '@/common/types/AllTypesExports';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';
import { Attendee, createAttendee, createAttendeeFromSelectListItemWithSubText } from '../../common/types/event/DtoAttendee';
import selectOnTab from '@/components/directives/selectOnTab-directive';

@Component({
    name: 'AttendeeSelector',
    components: {
        Multiselect,
        FieldValidationErrors
    },
    directives: {
        selectOnTab
    }
})
export default class AttendeeSelector extends Vue {

    // These are all attendees that may appear in the dropdown
    @Prop({ required: false, default: () => [] })
    eligibleAttendees!: Attendee[];

    // These are the attendees selected in the first step of attendee centric events
    @Prop({ required: false, default: () => [] })
    selectedAttendees!: Attendee[];

    // This is the current attendee in ticket based events - why do we need access to them?
    @Prop({ required: false, default: () => { } })
    currentAttendee!: Attendee;

    @Prop({ required: false, default: () => null })
    attendeeBased!: boolean;

    @Prop({ required: false, default: () => false })
    isRegistrationEditor!: boolean;

    @Prop({ required: false, default: false })
    allowGuests!: boolean;

    @Prop({ required: false, default: false })
    required!: boolean;

    @Prop({ required: false, default: () => null })
    validationScope!: string;

    @Prop({ required: false, default: () => false })
    disabled!: boolean;

    @Prop({ required: false, default: null })
    currentTicketId!: number | null;

    /** HTML id / vee-validate field name; must be unique when multiple selectors appear on one page. */
    @Prop({ required: false, default: 'attendeeSelector' })
    inputId!: string;

    @Watch('currentAttendee', { immediate: true, deep: true })
    onCurrentAttendeeChanged(): void {
        this.syncDropdownFromCurrentAttendee();
    }

    // Internal
    selectedAttendee: SelectListItemWithSubtext | null = null;
    loading = false;

    @Inject('validator')
    $validator: any;

    private normalizeText(val: any): string {
        if (val == null) return '';
        if (typeof val === 'string') return val;
        // object-like (Multiselect value)
        const obj = val as { text?: string; label?: string };
        return (obj && (obj.text || obj.label)) || '';
    } 

    private resetHandler = (isClear: boolean) => {
        if (isClear) {
            this.selectedAttendee = null;
        } else {
            this.selectedAttendee = createSelectListItemWithSubtextFromAttendee(this.currentAttendee);
        }
    }

    private syncDropdownFromCurrentAttendee(): void {
        if (!this.isRegistrationEditor) return;

        if (this.currentAttendee?.isTbd) {
            this.selectedAttendee = null;
            return;
        }

        let id = this.currentAttendee?.customerId;

        if (id === 0) {
            id = -1;
        }

        const match = this.options.find(o => o.id === id);

        this.selectedAttendee = (match ?? createSelectListItemWithSubtextFromAttendee(this.currentAttendee)) as any;
    }

    private isAlreadySelectedForCurrentTicket(optionAttendee: Attendee): boolean {
        if (this.attendeeBased) return false;
        if (!this.currentTicketId) return false;

        // In ticket-based flow, check if this attendee is already selected for the same ticket
        return (this.selectedAttendees || []).some(a => {
            if (!a) return false;

            // Skip current row
            if (this.currentAttendee && a.uniqueId && this.currentAttendee.uniqueId && a.uniqueId === this.currentAttendee.uniqueId) {
                return false;
            }

            // Same linked customer
            const samePerson =
                (a.customerId && optionAttendee.customerId && a.customerId === optionAttendee.customerId)
                // Guest fallback (no customerId)
                || (!a.customerId && !optionAttendee.customerId && (a.name || '').trim() === (optionAttendee.name || '').trim());

            if (!samePerson) return false;

            // Same ticket selected
            return (a.selectedTickets || []).some(t => t.id === this.currentTicketId);
        });
    }

    created() {
        this.$validator.extend('noAtAttendee', {
            getMessage: () => 'The "@" character is not allowed.',
            validate: (val: any) => !/@/.test(this.normalizeText(val))
        });
    }

    mounted() {
        var self = this;
        self.initSelectedAttendee();

        self.$root.$on('reset:attendee', this.resetHandler);
    }

    beforeDestroy() {
        var self = this;
        self.$root.$off('reset:attendee', this.resetHandler);
    }

    get options() {
        var ticketID = this.currentTicketId ?? (!this.attendeeBased && this.currentAttendee?.selectedTickets?.length ? this.currentAttendee.selectedTickets[0].id : null);
        var result = this.eligibleAttendees
            .filter(option => {
                // Exclude attendees from the dropdown that are already selected (in first step of attendee centric)
                return !this.attendeeBased || !this.selectedAttendees.some(selectedAttendee => selectedAttendee.matches(option));
            })
            .map(option => {
                // Map attendee to multiselect option model
                const item = new AttendeeSelectListItem(option, ticketID) as any;

                // Check duplicate selection for the same ticket (ticket-based flow)
                const duplicateForSameTicket = this.isAlreadySelectedForCurrentTicket(option);

                // Mark option as disabled in the dropdown
                item.isDisabled = duplicateForSameTicket || item.isEligible === false || item.hasAttended === true;

                if (duplicateForSameTicket) {
                    item.disabledReason = 'Already Registered';
                } else if (item.hasAttended) {
                    item.disabledReason = 'Already Attending';
                } else if (item.isEligible === false) {
                    item.disabledReason = 'Not Eligible';
                }

                return item;
            });

        // Prepend the list with the selected attendee if it's a guest
        if (!this.isRegistrationEditor && this.selectedAttendee && !this.selectedAttendee.id) {
            result.unshift(new AttendeeSelectListItem(createAttendeeFromSelectListItemWithSubText(this.selectedAttendee), ticketID));
        }

        return result;
    }

    get scopeStr(): string | undefined {
        const s = this.validationScope;
        return s === null || s === undefined || s === '' ? undefined : String(s);
    }

    onAttendeeUpdated(newValue: SelectListItemWithSubtext | null) {
        const option = newValue as any;

        if (option && option.isDisabled) {
            // Revert selection (or just clear it)
            this.$nextTick(() => {
                this.selectedAttendee = this.isRegistrationEditor
                    ? (createSelectListItemWithSubtextFromAttendee(this.currentAttendee) as any)
                    : null;
            });

            this.$validator.errors.add({
                field: this.inputId,
                scope: this.scopeStr,
                msg: `This attendee cannot be selected (${option.disabledReason || 'Restricted'}).`
            });

            return;
        }
        const text = this.normalizeText(newValue);

        if (!/@/.test(text)) {
            this.$validator.errors.remove(this.inputId, this.scopeStr);
        }

        this.$nextTick(() => {
            if (this.scopeStr) {
                this.$validator.validateAll(this.scopeStr);
            } else {
                this.$validator.validateAll();
            }
        });

        if (this.isRegistrationEditor) {
            if (newValue != null) {
                // if the first one is a guest, update it
                const first = this.eligibleAttendees?.[0];
                if (first && first.customerId === -1 && newValue.id === 0) {
                    first.name = newValue.text;
                    first.email = '';
                    first.image = newValue.image;
                }
            }
        }
        else {
            if (this.attendeeBased) {
                // Attendee selected
                if (newValue) {
                    this.selectedAttendee = null; // Clear the dropdown after selection - the selected member's badge will appear above the dropdown
                }
            }
            else {
                if (newValue != null) {
                    if (this.currentAttendee === undefined) {
                        this.currentAttendee = createAttendee();
                    }

                    // An attendee has just been selected and we're on the attendee form - populate the attendee's information
                    this.currentAttendee.customerId = newValue.id;
                    this.currentAttendee.name = newValue.text;
                    this.currentAttendee.email = newValue.subText?.includes(',') ? newValue.subText?.split(',')[0] : newValue.subText;
                    this.currentAttendee.image = newValue.image;

                    // If an existing customer was selected, then fetch their details so we can populate their information on the attendee form
                    var eligibleAttendee = newValue.id ? this.eligibleAttendees.find(a => a.customerId == this.currentAttendee.customerId) : null;
                    if (eligibleAttendee) {

                        // Populate member fields
                        this.currentAttendee.companyName = eligibleAttendee.companyName;
                        this.currentAttendee.jobTitle = eligibleAttendee.jobTitle;
                        this.currentAttendee.phone = eligibleAttendee.phone;
                        this.currentAttendee.mobilePhone = eligibleAttendee.mobilePhone;
                        this.currentAttendee.credentials = eligibleAttendee.credentials;
                        Object.assign(<DtoAddress>this.currentAttendee.shippingAddress, eligibleAttendee.shippingAddress);
                        this.currentAttendee.customerCustomFieldValues = { ...eligibleAttendee.customerCustomFieldValues };
                        this.currentAttendee.phoneIsInternational = !!eligibleAttendee.phoneIsInternational;
                        this.currentAttendee.mobilePhoneIsInternational = !!eligibleAttendee.mobilePhoneIsInternational;

                        // Populate custom fields
                        for (const customFieldID in this.currentAttendee.attendeeCustomFieldValues) {
                            const customerCustomField = this.currentAttendee.customerCustomFieldValues[customFieldID];
                            this.currentAttendee.attendeeCustomFieldValues[customFieldID].value = customerCustomField?.value;
                        }
                    }
                    else {
                        this.selectedAttendee = newValue;
                    }
                }
                else {
                    // Attendee removed
                    this.currentAttendee.customerId = null;
                    this.currentAttendee.name = "";
                    this.currentAttendee.email = "";
                    this.currentAttendee.image = "";
                    this.currentAttendee.companyName = "";
                    this.currentAttendee.jobTitle = "";
                    this.currentAttendee.phone = "";
                    this.currentAttendee.mobilePhone = "";
                    this.currentAttendee.credentials = "";
                    this.currentAttendee.shippingAddress = <DtoAddress>{
                        id: 0,
                        address1: null,
                        address2: null,
                        city: null,
                        zipCode: null,
                        stateProvince: null,
                        countryFullName: null,
                        country: null
                    };

                    for (const key in this.currentAttendee.attendeeCustomFieldValues) {
                        this.currentAttendee.attendeeCustomFieldValues[key].value = "";
                    }

                    this.currentAttendee.customerCustomFieldValues = {};

                    this.selectedAttendee = null;
                }
            }
        }

        this.$emit("update:attendee", newValue);
    }

    selectHighlightedOption() {
        var textEntered = $(this.$el).find(".multiselect__input").val();
        //@ts-ignore
        var isEnterEvent = event.which === 13;
        //@ts-ignore
        var isOptionSelectionEvent = event.currentTarget.className === 'multiselect__option multiselect__option--highlight';
        if (textEntered && !isEnterEvent && !isOptionSelectionEvent) {
            $(this.$el).find('.multiselect__option--highlight:not(.multiselect__option--selected)').trigger($.Event('click'));
        }
    }

    onAttendeeAdded(attendeeName: string) {
        const name = attendeeName || '';
        if (/@/.test(name)) {
            this.$validator.errors.add({
                field: this.inputId,
                scope: this.scopeStr,
                msg: 'The "@" character is not allowed.'
            });
            this.$nextTick(() => {
                if (this.scopeStr) {
                    this.$validator.validateAll(this.scopeStr);
                } else {
                    this.$validator.validateAll();
                }
            });
            return;
        }
        const addedAttendee = createSelectListItemWithSubtext({ text: attendeeName, image: '/img/missing-individual.jpg' });
        this.onAttendeeUpdated(addedAttendee);
    }

    initSelectedAttendee() {
        if (!this.attendeeBased && this.currentAttendee && this.currentAttendee.name && !this.currentAttendee.isTbd) {
            this.selectedAttendee = createSelectListItemWithSubtextFromAttendee(this.currentAttendee);
        }
    }

    getCustomerInitial(customerName: string | undefined, guestName: string | undefined) {
        if (customerName && customerName.length > 0) {
            return customerName.charAt(0);
        }
        if (guestName && guestName.length > 0) {
            return guestName.charAt(0);
        }

        return '';
    }
}